import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSupabaseClient } from "@supabase/auth-helpers-react";
import { MainLayout } from "@/components/layout/MainLayout";
import { Loader2, ArrowRight, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Tool } from "@/types";
import { GuideSection } from "@/components/GuideSection";
import { Button } from "@/components/ui/button";
import { ToolCard } from "@/components/ToolCard";
import { SEOHead } from "@/components/SEOHead";
import { SchemaMarkup } from "@/components/SchemaMarkup";

const ToolDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const supabase = useSupabaseClient();
  const [tool, setTool] = useState<Tool | null>(null);
  const [relatedTools, setRelatedTools] = useState<Tool[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchTool = async () => {
      try {
        const { data: toolData, error: toolError } = await supabase
          .from('tools')
          .select('*')
          .eq('id', id)
          .maybeSingle();

        if (toolError) throw toolError;
        if (!toolData) {
          toast.error("الأداة غير موجودة");
          navigate("/");
          return;
        }

        // Fetch likes count separately
        const { data: likesData } = await supabase
          .from('tool_interactions')
          .select('likes')
          .eq('tool_id', id);

        const totalLikes = likesData?.reduce((sum, curr) => sum + (curr.likes || 0), 0) || 0;

        const currentTool = {
          id: toolData.id,
          name: toolData.name,
          description: toolData.description,
          imageUrl: toolData.image_url,
          categories: toolData.categories || [],
          is_featured: toolData.is_featured || false,
          likes: totalLikes,
          created_at: toolData.created_at,
          updated_at: toolData.updated_at,
          usage_guide: toolData.usage_guide || '',
          tutorial_video_url: toolData.tutorial_video_url || '',
          tool_url: toolData.tool_url || '',
          examples: toolData.examples || []
        };

        setTool(currentTool);

        // Fetch related tools based on categories
        if (currentTool.categories.length > 0) {
          const { data: relatedData } = await supabase
            .from('tools')
            .select('*')
            .contains('categories', currentTool.categories)
            .neq('id', currentTool.id)
            .limit(3);

          if (relatedData) {
            setRelatedTools(relatedData.map(tool => ({
              id: tool.id,
              name: tool.name,
              description: tool.description,
              imageUrl: tool.image_url,
              categories: tool.categories,
              is_featured: tool.is_featured || false,
              tool_url: tool.tool_url,
              likes: 0
            })));
          }
        }

      } catch (error) {
        console.error("Error fetching tool:", error);
        toast.error("حدث خطأ أثناء تحميل الأداة");
        navigate("/");
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchTool();
    }
  }, [id, navigate, supabase]);

  const handleBackClick = () => {
    // Set the flag to indicate user explicitly wants to go to home
    sessionStorage.setItem('userRequestedHome', 'true');
    navigate("/");
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex justify-center items-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  if (!tool) return null;

  return (
    <>
      <SEOHead 
        title={`${tool.name} | دراية - Diraya.AI`}
        description={tool.description}
        imageUrl={tool.imageUrl}
        canonicalUrl={`https://diraya.ai/tool/${tool.id}`}
        type="article"
      />
      <SchemaMarkup currentTool={tool} isToolPage={true} />
      <MainLayout>
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Button variant="ghost" className="mb-6" onClick={handleBackClick}>
          <ArrowRight className="ml-2" />
          <span className="font-arabic">العودة للرئيسية</span>
        </Button>

        {/* Tool Header Section */}
        <div className="bg-card rounded-lg p-6 mb-8 shadow-sm">
          <div className="grid md:grid-cols-2 gap-6 items-start">
            {/* Image Column */}
            <div className="order-2 md:order-1">
              {tool.imageUrl && (
                <div className="aspect-video rounded-lg overflow-hidden bg-background">
                  <img 
                    src={tool.imageUrl} 
                    alt={tool.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>

            {/* Info Column */}
            <div className="order-1 md:order-2">
              <h1 className="text-3xl font-bold text-primary font-arabic mb-4">{tool.name}</h1>
              {tool.tool_url && (
                <a 
                  href={tool.tool_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors mb-4"
                >
                  <ExternalLink className="w-5 h-5" />
                  <span className="font-arabic">رابط الأداة</span>
                </a>
              )}
              <div className="prose prose-lg dark:prose-invert max-w-none font-arabic mb-4">
                <p>{tool.description}</p>
              </div>
              {tool.categories && tool.categories.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {tool.categories.map((category, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-arabic"
                    >
                      {category}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Guide Section */}
        <GuideSection tool={tool} />

        {/* Related Tools Section */}
        {relatedTools.length > 0 && (
          <div className="mt-12 bg-card rounded-lg p-6">
            <h2 className="text-2xl font-bold mb-6 text-primary font-arabic">أدوات مشابهة</h2>
            <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {relatedTools.map((relatedTool) => (
                <ToolCard 
                  key={relatedTool.id} 
                  {...relatedTool}
                  compact={true}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      </MainLayout>
    </>
  );
};

export default ToolDetails;
